import { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react'
import { Checkbox, FormControl, FormControlLabel, FormHelperText, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { ArrowDownwardOutlined, ArrowUpwardOutlined } from '@mui/icons-material'
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import ReductionScenarioService from '../../../shared/services/reduction-scenario/reduction-scenario.service'
import { IReductionScenarioProject } from '../../../shared/services/reduction-scenario/reduction-scenario.model'
import { segmentedToggleSx } from '../../../shared/utils/segmented-toggle-sx'
import { costPerTonnePerYear, formatCostPerTonne, SCOPE2_BASIS_HELP, SCOPE2_BASIS_LABELS } from '../../../shared/utils/reduction-utils'
import { Stack } from '@mui/material'

type Scope2Basis = 'market' | 'location' | 'both'
const isWholeNumber = (value: string) => value === '' || (Number.isInteger(Number(value)) && Number.isFinite(Number(value)))

export const ReductionScenarioProjectDialog: FunctionComponent<{
  open: boolean
  scenarioId: string
  project?: IReductionScenarioProject | null
  selectableYears: number[]
  closeHandler: () => void
  successHandler: () => void
}> = ({ open, scenarioId, project, selectableYears, closeHandler, successHandler }) => {
  const userNotificationsContext = useContext(UserNotificationsContext)
  const reductionScenarioService = new ReductionScenarioService()
  const isEdit = !!project

  const [name, setName] = useState('')
  const [scope, setScope] = useState<'1' | '2' | '3'>('1')
  // Direction is encoded as the sign of the stored annualReduction (negative = increase); the
  // dialog always shows a positive amount and a Decrease/Increase toggle.
  const [direction, setDirection] = useState<'decrease' | 'increase'>('decrease')
  const [annualReductionTce, setAnnualReductionTce] = useState<string>('')
  const [startYear, setStartYear] = useState<string>('')
  const [lifetimeYears, setLifetimeYears] = useState<string>('')
  const [isCumulative, setIsCumulative] = useState(false)
  const [isPermanent, setIsPermanent] = useState(false)
  const [scope2Basis, setScope2Basis] = useState<Scope2Basis>('both')
  const [capexGbp, setCapexGbp] = useState<string>('')
  const [annualOpexDeltaGbp, setAnnualOpexDeltaGbp] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      setName(project?.name ?? '')
      setScope((project?.scope as '1' | '2' | '3') ?? '1')
      setDirection(project?.annualReduction != null && Number(project.annualReduction) < 0 ? 'increase' : 'decrease')
      setAnnualReductionTce(project?.annualReduction != null ? String(Math.abs(Number(project.annualReduction)) / 1000) : '')
      setStartYear(project?.startYear != null ? String(project.startYear) : (selectableYears[0] != null ? String(selectableYears[0]) : ''))
      setLifetimeYears(project?.lifetimeYears != null ? String(project.lifetimeYears) : '')
      setIsCumulative(project?.isCumulative ?? false)
      setIsPermanent(project?.isPermanent ?? false)
      setScope2Basis((project?.scope2Basis as Scope2Basis) ?? 'both')
      setCapexGbp(project?.capexGbp != null ? String(project.capexGbp) : '')
      setAnnualOpexDeltaGbp(project?.annualOpexDeltaGbp != null ? String(project.annualOpexDeltaGbp) : '')
    }
  }, [open, project, selectableYears])

  // Always offer the measure's own start year, even if it falls outside the configured base..target range
  // (e.g. seeded from the plan or after base/target changed) — otherwise the Select renders blank.
  const yearOptions = useMemo(() => {
    const set = new Set<number>(selectableYears)
    if (project?.startYear != null) set.add(Number(project.startYear))
    return Array.from(set).sort((a, b) => a - b)
  }, [selectableYears, project])

  const annualReductionValid = annualReductionTce !== '' && Number(annualReductionTce) > 0
  const lifetimeValid = lifetimeYears === '' || (Number.isInteger(Number(lifetimeYears)) && Number(lifetimeYears) >= 1)
  const annualReductionError = annualReductionTce !== '' && !annualReductionValid
  const lifetimeError = lifetimeYears !== '' && !lifetimeValid
  const costsValid = isWholeNumber(capexGbp) && isWholeNumber(annualOpexDeltaGbp)
  const isValid = name.trim() !== '' && annualReductionValid && startYear !== '' && lifetimeValid && costsValid
  const costPerTonne = costPerTonnePerYear({
    capexGbp: capexGbp === '' ? null : Number(capexGbp),
    annualOpexDeltaGbp: annualOpexDeltaGbp === '' ? null : Number(annualOpexDeltaGbp),
    annualReductionKg: annualReductionTce === '' ? null : Number(annualReductionTce) * 1000,
    lifetimeYears: lifetimeYears === '' ? null : Number(lifetimeYears),
  })

  const handleSubmit = async () => {
    if (!isValid) return
    const payload = {
      name: name.trim(),
      scope,
      annualReduction: Number(annualReductionTce) * 1000 * (direction === 'increase' ? -1 : 1),
      startYear: Number(startYear),
      lifetimeYears: lifetimeYears === '' ? null : Number(lifetimeYears),
      isCumulative,
      isPermanent,
      scope2Basis: scope === '2' ? scope2Basis : 'both',
      capexGbp: capexGbp === '' ? null : Number(capexGbp),
      annualOpexDeltaGbp: annualOpexDeltaGbp === '' ? null : Number(annualOpexDeltaGbp),
    }
    try {
      setIsSubmitting(true)
      if (isEdit && project) {
        await reductionScenarioService.updateScenarioProject(scenarioId, project.id, payload)
      } else {
        await reductionScenarioService.createScenarioProject(scenarioId, payload)
      }
      successHandler()
      closeHandler()
    } catch (e: any) {
      userNotificationsContext.openSnackbar({
        open: true,
        message: e?.response?.data?.message || 'Something went wrong',
        backgroundColor: 'text.primary',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <DialogWrapper
      open={open}
      onClose={closeHandler}
      title={isEdit ? 'Edit Measure' : 'New Measure'}
      cancelHandler={closeHandler}
      submitHandler={handleSubmit}
      isSubmitting={isSubmitting}
      disableSaveButton={!isValid}
      maxWidth='sm'
    >
      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
        <InputLabel htmlFor='project-name'>Measure Name</InputLabel>
        <OutlinedInput id='project-name' label='Measure Name' value={name} onChange={(e) => setName(e.target.value)} />
      </FormControl>

      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
        <InputLabel htmlFor='project-scope'>Scope</InputLabel>
        <Select id='project-scope' label='Scope' value={scope} onChange={(e) => setScope(e.target.value as '1' | '2' | '3')}>
          <MenuItem value='1'>Scope 1</MenuItem>
          <MenuItem value='2'>Scope 2</MenuItem>
          <MenuItem value='3'>Scope 3</MenuItem>
        </Select>
      </FormControl>

      {scope === '2' && (
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <InputLabel htmlFor='project-scope2-basis'>Scope 2 basis</InputLabel>
          <Select id='project-scope2-basis' label='Scope 2 basis' value={scope2Basis} onChange={(e) => setScope2Basis(e.target.value as Scope2Basis)}>
            {(Object.keys(SCOPE2_BASIS_LABELS) as Scope2Basis[]).map((basis) => (
              <MenuItem key={basis} value={basis}>{SCOPE2_BASIS_LABELS[basis]}</MenuItem>
            ))}
          </Select>
          <FormHelperText>{SCOPE2_BASIS_HELP[scope2Basis]}</FormHelperText>
        </FormControl>
      )}

      <FormControl fullWidth sx={{ mt: 2 }}>
        <ToggleButtonGroup
          value={direction}
          exclusive
          fullWidth
          onChange={(_event, newValue) => {
            if (newValue) setDirection(newValue)
          }}
          size='small'
          aria-label='Measure direction'
          sx={segmentedToggleSx}
        >
          <ToggleButton value='decrease' aria-label='Models an emissions decrease'>
            <ArrowDownwardOutlined sx={{ fontSize: '18px', mr: 0.75, color: 'success.main' }} />
            Decrease emissions
          </ToggleButton>
          <ToggleButton value='increase' aria-label='Models an emissions increase'>
            <ArrowUpwardOutlined sx={{ fontSize: '18px', mr: 0.75, color: 'error.main' }} />
            Increase emissions
          </ToggleButton>
        </ToggleButtonGroup>
        <FormHelperText>
          {direction === 'increase'
            ? 'Model a discrete emissions increase (a new site, equipment or expansion), raising the scenario line above the baseline. For general business growth, set the growth assumption on the scenario instead.'
            : 'Model an emissions saving, lowering the scenario line below the baseline.'}
        </FormHelperText>
      </FormControl>

      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }} error={annualReductionError}>
        <InputLabel htmlFor='project-annual-reduction'>{direction === 'increase' ? 'Annual Increase' : 'Annual Reduction'}</InputLabel>
        <OutlinedInput
          id='project-annual-reduction'
          label={direction === 'increase' ? 'Annual Increase' : 'Annual Reduction'}
          type='number'
          value={annualReductionTce}
          error={annualReductionError}
          onChange={(e) => setAnnualReductionTce(e.target.value)}
          endAdornment={<InputAdornment position='end'><Typography variant='body2' sx={{ color: 'text.secondary' }}>tCO<sub>2</sub>e</Typography></InputAdornment>}
          inputProps={{ step: '0.01', min: 0 }}
        />
        <FormHelperText>
          {annualReductionError
            ? 'Enter a value greater than 0.'
            : (direction === 'increase'
              ? 'Estimated additional emissions each year while this measure is active.'
              : 'Estimated emissions avoided each year while this measure is active.')}
        </FormHelperText>
      </FormControl>

      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
        <InputLabel htmlFor='project-start-year'>Start Year</InputLabel>
        <Select id='project-start-year' label='Start Year' value={startYear} onChange={(e) => setStartYear(String(e.target.value))}>
          {yearOptions.map((year) => (
            <MenuItem key={year} value={String(year)}>{year}</MenuItem>
          ))}
        </Select>
        <FormHelperText>The year this measure starts affecting projected emissions.</FormHelperText>
      </FormControl>

      <FormControl fullWidth variant='outlined' sx={{ mt: 2 }} error={lifetimeError}>
        <InputLabel htmlFor='project-lifetime'>Lifetime (years)</InputLabel>
        <OutlinedInput
          id='project-lifetime'
          label='Lifetime (years)'
          type='number'
          value={lifetimeYears}
          error={lifetimeError}
          onChange={(e) => setLifetimeYears(e.target.value)}
          placeholder='Ongoing'
          inputProps={{ step: 1, min: 1 }}
        />
        <FormHelperText>{lifetimeError ? 'Enter a whole number of years (1 or more), or leave blank for ongoing.' : 'Number of years this measure is active. Leave blank if ongoing.'}</FormHelperText>
      </FormControl>

      <FormControl fullWidth sx={{ mt: 1.5 }}>
        <FormControlLabel
          control={<Checkbox checked={isCumulative} onChange={(e) => setIsCumulative(e.target.checked)} />}
          label='Cumulative: stacks each year'
        />
        <FormHelperText sx={{ mt: -0.5 }}>
          Each active year adds another {annualReductionTce !== '' ? `${annualReductionTce} ` : ''}tCO<sub>2</sub>e
          on top of the last, instead of a flat annual {direction === 'increase' ? 'increase' : 'saving'}.
        </FormHelperText>
      </FormControl>

      <FormControl fullWidth sx={{ mt: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isPermanent}
              onChange={(e) => setIsPermanent(e.target.checked)}
              disabled={lifetimeYears === ''}
            />
          }
          label='Permanent: does not revert'
        />
        <FormHelperText sx={{ mt: -0.5 }}>
          {lifetimeYears === ''
            ? 'An ongoing measure never reverts, so this only applies when a lifetime is set.'
            : `The ${direction === 'increase' ? 'added emissions are' : 'saving is'} kept after the lifetime ends, instead of reverting.`}
        </FormHelperText>
      </FormControl>

      {direction === 'decrease' && (
        <>
          <Typography variant='subtitle2' sx={{ mt: 3, mb: 1, color: 'text.secondary' }}>Cost (optional)</Typography>
          <Stack direction='row' spacing={2}>
            <FormControl fullWidth variant='outlined' error={!isWholeNumber(capexGbp)}>
              <InputLabel htmlFor='project-capex'>Capital cost</InputLabel>
              <OutlinedInput id='project-capex' label='Capital cost' type='number' value={capexGbp} onChange={(e) => setCapexGbp(e.target.value)}
                startAdornment={<InputAdornment position='start'>£</InputAdornment>} inputProps={{ step: 1, min: 0 }} />
              <FormHelperText>One-off, whole pounds.</FormHelperText>
            </FormControl>
            <FormControl fullWidth variant='outlined' error={!isWholeNumber(annualOpexDeltaGbp)}>
              <InputLabel htmlFor='project-opex'>Running cost change</InputLabel>
              <OutlinedInput id='project-opex' label='Running cost change' type='number' value={annualOpexDeltaGbp} onChange={(e) => setAnnualOpexDeltaGbp(e.target.value)}
                startAdornment={<InputAdornment position='start'>£</InputAdornment>}
                endAdornment={<InputAdornment position='end'><Typography variant='body2' sx={{ color: 'text.secondary' }}>a year</Typography></InputAdornment>}
                inputProps={{ step: 1 }} />
              <FormHelperText>Negative for a saving on running costs.</FormHelperText>
            </FormControl>
          </Stack>
          {costPerTonne != null && (
            <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1.5 }}>Cost per tonne: {formatCostPerTonne(costPerTonne)}.</Typography>
          )}
        </>
      )}
    </DialogWrapper>
  )
}
